import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Repeat, ArrowUpRight } from 'lucide-react';
import api from '../lib/api';

/**
 * UpcomingRibbon — Q32 Home page compact CTA.
 *
 * Single-card teaser instead of the 3-card list on Home. Two variants:
 *
 *  • 0 upcoming  → render nothing
 *  • 1 upcoming  → "📅 Next Up" + title + relative time + Open button
 *  • 2+          → "You have X upcoming plans this month" + next preview + View Calendar
 *
 * Profile keeps the larger <UpcomingWidget/>. Home should stay
 * discovery-first; the ribbon nudges, the full agenda lives elsewhere.
 */
export default function UpcomingRibbon() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Need enough to know "this month" — fetch 30 then filter client-side
    api.get('/calendar/upcoming?limit=30')
      .then(r => setItems(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || items.length === 0) return null;

  const next = items[0];
  const thisMonth = countThisMonth(items);
  const hasMany = thisMonth > 1;

  return (
    <section data-testid="upcoming-ribbon" className="px-4 mb-5">
      <button
        data-testid="upcoming-ribbon-card"
        onClick={() => navigate(hasMany ? '/calendar' : `/plan/${next.plan.id}`)}
        className="w-full bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors text-left"
      >
        <div className="w-11 h-11 rounded-xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center flex-shrink-0">
          <Calendar size={18} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          {hasMany ? (
            <>
              <p data-testid="ribbon-count" className="text-[11px] font-extrabold uppercase tracking-wider text-[#3F5200]">
                {thisMonth} upcoming this month
              </p>
              <p className="text-sm font-extrabold text-[#111] truncate flex items-center gap-1">
                Next: {next.plan.title}
                {next.is_recurring && <Repeat size={11} className="text-[#3F5200]" strokeWidth={2.6} />}
              </p>
              <p className="text-[11px] text-[#6B6E73] truncate">
                {prettyWhen(next.date, next.plan.start_time)}
              </p>
            </>
          ) : (
            <>
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#3F5200]">
                Next up
              </p>
              <p data-testid="ribbon-next-title" className="text-sm font-extrabold text-[#111] truncate flex items-center gap-1">
                {next.plan.title}
                {next.is_recurring && <Repeat size={11} className="text-[#3F5200]" strokeWidth={2.6} />}
              </p>
              <p className="text-[11px] text-[#6B6E73] truncate">
                {prettyWhen(next.date, next.plan.start_time)}
              </p>
            </>
          )}
        </div>
        <div
          data-testid={hasMany ? 'ribbon-view-calendar' : 'ribbon-open-event'}
          className="flex items-center gap-1 text-[11px] font-extrabold text-[#111] bg-[#B6E600] rounded-full px-3 py-1.5 flex-shrink-0"
        >
          {hasMany ? 'Calendar' : 'Open'}
          <ArrowUpRight size={12} strokeWidth={2.8} />
        </div>
      </button>
    </section>
  );
}

function countThisMonth(items) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  return items.filter(r => {
    if (!r.date) return false;
    const [yy, mm] = r.date.split('-').map(s => parseInt(s, 10));
    return yy === y && mm - 1 === m;
  }).length;
}

function prettyWhen(iso, startTime) {
  try {
    const d = new Date(iso + 'T00:00:00');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tom = new Date(today); tom.setDate(tom.getDate() + 1);
    const label = d.getTime() === today.getTime() ? 'Today'
      : d.getTime() === tom.getTime() ? 'Tomorrow'
      : d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    const t = formatTime12(startTime);
    return t ? `${label} · ${t}` : label;
  } catch { return iso || ''; }
}

function formatTime12(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return '';
  const [h, m] = hhmm.split(':');
  const hr = parseInt(h, 10);
  if (Number.isNaN(hr)) return '';
  const period = hr >= 12 ? 'PM' : 'AM';
  const display = ((hr + 11) % 12) + 1;
  const minutes = (m || '00').slice(0, 2);
  return minutes === '00' ? `${display} ${period}` : `${display}:${minutes} ${period}`;
}
